/**
 * Cosa è in ritardo, commessa per commessa.
 *
 * Un numero in dashboard — "3.300 € di anni precedenti" — non dice cosa fare.
 * Qui si vede da dove viene: quale cliente, da quanto è ferma, se c'è una
 * fattura già in coda da emettere o solo un residuo di contratto mai
 * rendicontato, e si decide sul posto: fatturare il saldo, o chiudere la
 * commessa lasciando andare il residuo.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fatturazionIApi, commesseApi } from '../api';
import Modal from './Modal';
import CampoImporto from './CampoImporto';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const mesi = (g) => g === null || g === undefined ? null
  : g < 60 ? `${g} giorni` : `${Math.round(g / 30)} mesi`;

export default function InRitardo({ onCambiato, clienteId }) {
  const [dati0, setDati] = useState(null);
  const [chiusura, setChiusura] = useState(null);
  const [lavorando, setLavorando] = useState(false);

  const carica = () => fatturazionIApi.inRitardo().then(setDati).catch(() => setDati(null));
  useEffect(() => { carica(); }, []);

  if (!dati0) return <div className="text-sm text-gray-400 py-8 text-center">Caricamento…</div>;

  // Il filtro cliente della pagina vale anche qui: con un cliente scelto i
  // tre totali si rifanno sulle sue sole commesse, altrimenti direbbero una
  // cosa e la tabella un'altra.
  const annoCorrente = new Date().getFullYear();
  const dati = !clienteId ? dati0 : (() => {
    const righe = dati0.righe.filter(r => String(r.cliente_id) === String(clienteId));
    const somma = (lista, campo) => +lista.reduce((t, x) => t + Number(x[campo] || 0), 0).toFixed(2);
    return {
      righe,
      totale: somma(righe, 'residuo_totale'),
      anni_precedenti: somma(righe.filter(r => r.anno < annoCorrente), 'residuo_totale'),
      scadute: somma(righe.filter(r => r.giorni_di_ritardo), 'in_coda'),
    };
  })();

  if (!dati.righe.length) {
    return (
      <div className="card text-center py-10 text-gray-400">
        {clienteId
          ? 'Niente in ritardo per questo cliente.'
          : 'Niente in ritardo: ogni commessa con un residuo è stata fatturata di recente.'}
      </div>
    );
  }

  /** Chiusura: se resta qualcosa da fatturare lo si chiede prima. */
  const apriChiusura = async (r) => {
    try {
      const rp = await commesseApi.riepilogoChiusura(r.commessa_id);
      setChiusura({
        ...rp, commessa_id: r.commessa_id, numero: r.numero, cliente: r.cliente_nome,
        importo: String(rp.residuo || 0), crea: !!rp.proponi_fattura,
        descrizione: `Saldo a chiusura — ${rp.titolo || ''}`.trim(),
      });
    } catch (e) { alert(e); }
  };

  const confermaChiusura = async () => {
    setLavorando(true);
    try {
      await commesseApi.chiudi(chiusura.commessa_id, chiusura.crea ? {
        fattura_residuo: true,
        importo_residuo: Number(chiusura.importo),
        descrizione_residuo: chiusura.descrizione,
      } : {});
      setChiusura(null);
      await carica();
      onCambiato?.();
    } catch (e) { alert(e); } finally { setLavorando(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Residuo in ritardo</div>
          <div className="text-2xl font-bold text-red-600 mt-1">{fmt(dati.totale)}</div>
          <div className="text-xs text-gray-400 mt-0.5">{dati.righe.length} commesse</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Di anni precedenti</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{fmt(dati.anni_precedenti)}</div>
          <div className="text-xs text-gray-400 mt-0.5">commesse avviate prima di quest'anno</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Fatture in coda scadute</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{fmt(dati.scadute)}</div>
          <div className="text-xs text-gray-400 mt-0.5">decise, mai emesse: basta spuntarle</div>
        </div>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-3">Commessa</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3 text-right">In coda</th>
              <th className="px-4 py-3 text-right">Residuo aperto</th>
              <th className="px-4 py-3">Ultima fattura</th>
              <th className="px-4 py-3">Perché è in ritardo</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {dati.righe.map(r => (
              <tr key={r.commessa_id} className={`border-b last:border-0 hover:bg-gray-50 ${
                r.anno < new Date().getFullYear() ? 'bg-red-50/40' : ''}`}>
                <td className="px-4 py-3">
                  <Link to={`/commesse/${r.commessa_id}`} className="font-mono text-xs text-brand-600 hover:underline">
                    {r.numero?.trim() || '(senza numero)'}
                  </Link>
                  <div className="text-xs text-gray-500 truncate max-w-[220px]">{r.titolo}</div>
                  <div className="text-[11px] text-gray-400">
                    {r.anno} · {r.stato}{r.periodicita_mesi ? ` · ogni ${r.periodicita_mesi} mesi` : ''}
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-700">{r.cliente_nome || '—'}</td>
                <td className="px-4 py-3 text-right">
                  {Number(r.in_coda) > 0 ? (
                    <>
                      <span className="font-semibold text-amber-700">{fmt(r.in_coda)}</span>
                      <div className="text-[11px] text-gray-500">
                        {r.righe_in_coda} {r.righe_in_coda === 1 ? 'fattura' : 'fatture'}
                        {r.giorni_di_ritardo ? ` · scaduta da ${mesi(r.giorni_di_ritardo)}` : ''}
                      </div>
                    </>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  {Number(r.residuo_aperto) > 0
                    ? <span className="font-medium text-gray-700">{fmt(r.residuo_aperto)}</span>
                    : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {r.ultima_fattura ? (
                    <>
                      {fmtDate(r.ultima_fattura)}
                      <div className="text-[11px] text-gray-400">{mesi(r.giorni_da_ultima_fattura)} fa</div>
                    </>
                  ) : <span className="text-red-700">mai fatturata</span>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600 max-w-[260px]">
                  {r.motivi.join(' · ')}
                  <div className="text-[11px] text-brand-700 mt-0.5">→ {r.suggerimento}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {Number(r.in_coda) > 0 && (
                      <Link to="/fatturazioni" className="btn-secondary btn-sm" title="Vai al riepilogo del mese per spuntarla">
                        Emetti
                      </Link>
                    )}
                    {r.sal_aperti > 0 && (
                      <Link to={`/sal?commessa=${r.commessa_id}`} className="btn-secondary btn-sm">
                        {r.sal_aperti} SAL
                      </Link>
                    )}
                    {r.stato === 'attiva' && (
                      <button className="btn-sm btn bg-green-100 text-green-700 hover:bg-green-200 font-semibold"
                              onClick={() => apriChiusura(r)}
                              title="Chiudi la commessa: potrai fatturare il saldo o lasciarlo andare">
                        Chiudi
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-500">
        "In coda" sono fatture già decise e mai emesse: si spuntano nel riepilogo del mese.
        "Residuo aperto" è contratto non ancora rendicontato: si fattura come saldo o si
        lascia andare chiudendo la commessa.
      </p>

      {chiusura && (
        <Modal title={`Chiudi ${chiusura.numero} — ${chiusura.cliente || ''}`} onClose={() => setChiusura(null)}>
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 border rounded-lg p-3">
              <div>Valore della commessa: <strong>{fmt(chiusura.budget)}</strong></div>
              <div>Già fatturato: <strong className="text-brand-700">{fmt(chiusura.fatturato)}</strong></div>
              {Number(chiusura.gia_da_emettere) > 0 && (
                <div>Già in coda: <strong className="text-amber-700">{fmt(chiusura.gia_da_emettere)}</strong></div>
              )}
              <div className="mt-1 pt-1 border-t">
                Resterebbe da fatturare: <strong className={chiusura.residuo > 0 ? 'text-red-600' : 'text-gray-500'}>
                  {fmt(chiusura.residuo)}
                </strong>
              </div>
            </div>

            {Number(chiusura.residuo) > 0 ? (
              <>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-1" checked={chiusura.crea}
                         onChange={e => setChiusura(c => ({ ...c, crea: e.target.checked }))} />
                  <span>
                    Fattura il saldo chiudendo
                    <span className="block text-xs text-gray-500">
                      Togli la spunta per chiudere lasciando andare il residuo: non verrà più fatturato.
                    </span>
                  </span>
                </label>
                {chiusura.crea && (
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="label text-xs">Importo</label>
                      <CampoImporto className="input py-1 text-sm" value={chiusura.importo}
                                    onChange={v => setChiusura(c => ({ ...c, importo: v }))} />
                    </div>
                    <div className="col-span-2">
                      <label className="label text-xs">Descrizione in fattura</label>
                      <input className="input py-1 text-sm" value={chiusura.descrizione}
                             onChange={e => setChiusura(c => ({ ...c, descrizione: e.target.value }))} />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-600">Niente da fatturare: la commessa si chiude e basta.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setChiusura(null)}>Annulla</button>
            <button className="btn-primary" disabled={lavorando} onClick={confermaChiusura}>
              {lavorando ? 'Chiudo…' : '✓ Chiudi la commessa'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
